/**
 * Phase 2 (Feb 2026) — Dual-Transport Session Storage.
 *
 * iOS Safari periodically evicts HttpOnly cookies from installed PWAs
 * (ITP + Storage Access rules + storage pressure), which forces users to
 * re-login even though their session is still valid on the server. This
 * module owns the JS-accessible half of the auth transport:
 *
 *   - `session_token` — mirrors the 1h access cookie, sent as
 *     `Authorization: Bearer …` when cookies are unavailable.
 *   - `refresh_public` — 90d bearer with a DISTINCT `type=refresh_public`
 *     JWT claim so it can never be used as a cookie refresh token
 *     server-side. Redeemable only via POST body at /auth/refresh and
 *     /auth/session. Rotated on every use.
 *
 * ─── Security ──────────────────────────────────────────────────────────
 * Threat model. These tokens live in `localStorage`, which is JS-reachable.
 * XSS on our domain would expose them. Mitigations already in place:
 *   - CSP + React auto-escaping across every rendered surface.
 *   - HttpOnly cookies remain the PRIMARY transport; the localStorage
 *     pair is a fallback that only unblocks the cookie-evicted user.
 *   - Backend enforces distinct JWT `type` claims — a stolen public
 *     refresh token cannot be laundered into a cookie refresh.
 *   - Token rotation on every /auth/session call bounds the effective
 *     lifetime of a leaked entry to the visibilitychange cadence.
 *   - `saveSession()` refuses to persist tokens that don't parse or
 *     that expire in the past; a corrupted entry can never resurrect
 *     a real session.
 *   - Namespaced key (`am_session_v1`) so schema changes can migrate
 *     without stepping on prior clients.
 *
 * Never stored: password, referral code, PII beyond user_id.
 * ─────────────────────────────────────────────────────────────────────
 */

const STORAGE_KEY = 'am_session_v1';

/**
 * Read the persisted session envelope, if any.
 * Returns null on any of: no entry, non-JSON, missing fields, expired.
 * NEVER throws.
 *
 * @returns {{session_token: string, refresh_public: string, saved_at: number}|null}
 */
export function loadSession() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const { session_token, refresh_public, saved_at } = parsed;
    if (typeof session_token !== 'string' || !session_token) return null;
    if (typeof refresh_public !== 'string' || !refresh_public) return null;
    // If the refresh_public has an exp claim and it's in the past,
    // discard proactively — no need to make the server tell us it's dead.
    const rExp = decodeJwtExp(refresh_public);
    if (rExp !== null && rExp < nowSec()) {
      clearSession();
      return null;
    }
    return {
      session_token,
      refresh_public,
      saved_at: typeof saved_at === 'number' ? saved_at : 0,
    };
  } catch (_) {
    // Corrupted entry — wipe it so future reads don't keep tripping.
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* noop */ }
    return null;
  }
}

/**
 * Persist a fresh session envelope. Called after any auth response
 * (login/register/refresh/session heartbeat).
 *
 * @param {{session_token?: string, refresh_public?: string}} payload
 */
export function saveSession(payload) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  if (!payload || typeof payload !== 'object') return;
  const { session_token, refresh_public } = payload;
  // Only persist if BOTH halves are present — a partial entry would
  // half-authenticate on a cookie-evicted session, causing worse UX
  // than a clean logout.
  if (typeof session_token !== 'string' || !session_token) return;
  if (typeof refresh_public !== 'string' || !refresh_public) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      session_token,
      refresh_public,
      saved_at: Date.now(),
    }));
  } catch (_) {
    // Storage quota / private-mode — non-fatal, cookies stay authoritative.
  }
}

/**
 * Clear the persisted envelope. Called on logout, cross-account
 * transition, and hard refresh failure.
 */
export function clearSession() {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try { window.localStorage.removeItem(STORAGE_KEY); } catch (_) { /* noop */ }
}

/**
 * Return the current session_token if it exists AND hasn't obviously
 * expired. Used by the axios interceptor to attach the Bearer header.
 * NEVER throws.
 */
export function currentBearer() {
  const s = loadSession();
  if (!s) return null;
  const exp = decodeJwtExp(s.session_token);
  if (exp !== null && exp < nowSec()) return null;
  return s.session_token;
}

/**
 * Return the current refresh_public if valid. Used ONLY inside the
 * refresh/session request body on cookie-evicted retries.
 */
export function currentPublicRefresh() {
  const s = loadSession();
  return s ? s.refresh_public : null;
}

// ─── internals ────────────────────────────────────────────────────────

function nowSec() { return Math.floor(Date.now() / 1000); }

/**
 * Extract the `exp` claim from a JWT WITHOUT verifying the signature —
 * we only use it to decide whether to bother sending a token that's
 * clearly expired. The server is the source of truth for validation.
 * Returns null if the token isn't a well-formed JWT.
 */
function decodeJwtExp(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // base64url → base64 → JSON
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch (_) {
    return null;
  }
}

// Exported for tests / debug consoles.
export const __STORAGE_KEY = STORAGE_KEY;
